(() => {
  if (customElements.get("p-banner")) return;

  class PBanner extends HTMLElement {
    connectedCallback() {
      if (this.inited) return;
      this.inited = true;
      this.initResponsiveVideo();
      this.initHoverTexts();
    }

    disconnectedCallback() {
      this.destroyVideo();
      this.destroyHoverTexts();
      this.inited = false;
    }

    addMqlListener(mql, handler) {
      if (!mql || !handler) return;
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", handler);
        return;
      }
      if (typeof mql.addListener === "function") {
        mql.addListener(handler);
      }
    }

    removeMqlListener(mql, handler) {
      if (!mql || !handler) return;
      if (typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", handler);
        return;
      }
      if (typeof mql.removeListener === "function") {
        mql.removeListener(handler);
      }
    }

    initResponsiveVideo() {
      this.videoBox = this.querySelector("[data-p-banner-video]");
      if (!this.videoBox) return;

      this.video = this.videoBox.querySelector("video");
      if (!this.video) return;

      this.mql = window.matchMedia("(max-width: 767px)");
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.bufferRatio = 0.5;
      this.appliedSrc = "";
      this.canPlay = false;
      this.isInView = false;

      this.onTryPlay = this.tryPlay.bind(this);
      this.onApplySource = this.applyVideoSource.bind(this);

      this.video.addEventListener("progress", this.onTryPlay);
      this.video.addEventListener("loadedmetadata", this.onTryPlay);
      this.video.addEventListener("canplaythrough", this.onTryPlay);

      this.addMqlListener(this.mql, this.onApplySource);
      this.addMqlListener(this.reducedMotion, this.onApplySource);

      if ("IntersectionObserver" in window) {
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            this.isInView = entry.isIntersecting;
            if (this.isInView) {
              this.applyVideoSource();
              return;
            }
            this.pauseVideo();
          },
          { rootMargin: "200px 0px", threshold: 0.01 }
        );
        this.videoObserver.observe(this.videoBox);
        return;
      }

      this.isInView = true;
      this.applyVideoSource();
    }

    resetPlayingState() {
      this.canPlay = false;
      if (this.videoBox) this.videoBox.classList.remove("is-playing");
    }

    isHalfBuffered() {
      if (!this.video) return false;
      const duration = this.video.duration;
      if (!duration || !isFinite(duration) || duration <= 0) return false;
      if (!this.video.buffered || this.video.buffered.length === 0) return false;
      return this.video.buffered.end(this.video.buffered.length - 1) >= duration * this.bufferRatio;
    }

    startPlay() {
      if (!this.video || this.canPlay || this.reducedMotion.matches || !this.isInView) return;
      this.canPlay = true;
      this.video
        .play()
        .then(() => {
          if (this.videoBox) this.videoBox.classList.add("is-playing");
        })
        .catch(() => {
          this.canPlay = false;
        });
    }

    tryPlay() {
      if (this.canPlay || !this.video || this.reducedMotion.matches || !this.isInView) return;
      if (this.isHalfBuffered()) {
        this.startPlay();
        return;
      }
      if ((!this.video.buffered || this.video.buffered.length === 0) && this.video.readyState >= 4) {
        this.startPlay();
      }
    }

    pauseVideo() {
      if (!this.video) return;
      this.video.pause();
      this.canPlay = false;
    }

    applyVideoSource() {
      if (!this.video || !this.videoBox) return;

      if (this.reducedMotion.matches) {
        this.video.pause();
        this.resetPlayingState();
        return;
      }

      if (!this.isInView) {
        this.pauseVideo();
        return;
      }

      const useMobile = this.mql.matches && Boolean(this.videoBox.dataset.srcM);
      const src = useMobile
        ? this.videoBox.dataset.srcM
        : this.videoBox.dataset.srcPc || this.videoBox.dataset.srcM;
      const type = useMobile
        ? this.videoBox.dataset.typeM || "video/mp4"
        : this.videoBox.dataset.typePc || this.videoBox.dataset.typeM || "video/mp4";

      if (!src || src === this.appliedSrc) {
        this.tryPlay();
        return;
      }

      this.appliedSrc = src;
      this.resetPlayingState();
      this.video.pause();
      this.video.removeAttribute("src");
      while (this.video.firstChild) this.video.removeChild(this.video.firstChild);

      const source = document.createElement("source");
      source.src = src;
      source.type = type;
      this.video.appendChild(source);
      this.video.load();
      this.tryPlay();
    }

    destroyVideo() {
      this.pauseVideo();

      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }

      if (this.video && this.onTryPlay) {
        this.video.removeEventListener("progress", this.onTryPlay);
        this.video.removeEventListener("loadedmetadata", this.onTryPlay);
        this.video.removeEventListener("canplaythrough", this.onTryPlay);
      }

      this.removeMqlListener(this.mql, this.onApplySource);
      this.removeMqlListener(this.reducedMotion, this.onApplySource);

      this.videoBox = null;
      this.video = null;
      this.mql = null;
      this.reducedMotion = null;
      this.onTryPlay = null;
      this.onApplySource = null;
      this.appliedSrc = "";
    }

    initHoverTexts() {
      this.hoverCleanups = [];
      this.querySelectorAll(".p-banner__hover-text").forEach((wrap) => {
        this.initHoverText(wrap);
      });
    }

    initHoverText(hoverTextWrap) {
      if (!hoverTextWrap || hoverTextWrap.dataset.hoverInited === "true") return;

      const allHoverTextItems = Array.from(hoverTextWrap.querySelectorAll(".p-banner__hover-text-item"));
      if (!allHoverTextItems.length) return;

      hoverTextWrap.dataset.hoverInited = "true";

      const isVisible = (item) => getComputedStyle(item).display !== "none";
      const interval = 2000;
      let visibleItems = [];
      let currentIndex = 0;
      let timer = null;
      let resizeTimer = null;

      const syncVisibleItems = () => {
        allHoverTextItems.forEach((item) => {
          if (!isVisible(item)) item.classList.remove("is-active");
        });

        visibleItems = allHoverTextItems.filter(isVisible);
        if (!visibleItems.length) return false;

        const activeIndex = visibleItems.findIndex((item) => item.classList.contains("is-active"));
        if (activeIndex >= 0) {
          currentIndex = activeIndex;
        } else {
          currentIndex = 0;
          visibleItems.forEach((item, index) => {
            item.classList.toggle("is-active", index === currentIndex);
          });
        }
        return true;
      };

      const setActive = (nextIndex) => {
        if (!visibleItems.length) return false;

        const safeIndex = (nextIndex + visibleItems.length) % visibleItems.length;
        if (safeIndex === currentIndex) return false;

        visibleItems[currentIndex].classList.remove("is-active");
        visibleItems[safeIndex].classList.add("is-active");
        currentIndex = safeIndex;
        return true;
      };

      const scheduleRotate = () => {
        clearTimeout(timer);
        if (!visibleItems.length) return;

        timer = setTimeout(() => {
          setActive(currentIndex + 1);
          scheduleRotate();
        }, interval);
      };

      if (!syncVisibleItems()) return;

      scheduleRotate();

      const onMouseEnter = (event) => {
        if (!syncVisibleItems()) return;
        const index = visibleItems.indexOf(event.currentTarget);
        if (index < 0) return;
        if (!setActive(index)) return;
        scheduleRotate();
      };

      allHoverTextItems.forEach((item) => {
        item.addEventListener("mouseenter", onMouseEnter);
      });

      const onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (!syncVisibleItems()) {
            clearTimeout(timer);
            return;
          }
          scheduleRotate();
        }, 150);
      };

      window.addEventListener("resize", onResize, { passive: true });

      this.hoverCleanups.push(() => {
        clearTimeout(timer);
        clearTimeout(resizeTimer);
        allHoverTextItems.forEach((item) => {
          item.removeEventListener("mouseenter", onMouseEnter);
        });
        window.removeEventListener("resize", onResize);
        hoverTextWrap.dataset.hoverInited = "false";
      });
    }

    destroyHoverTexts() {
      if (!this.hoverCleanups) return;
      this.hoverCleanups.forEach((cleanup) => cleanup());
      this.hoverCleanups = [];
    }
  }

  customElements.define("p-banner", PBanner);
})();
